/**
 * AI Search Evaluation Form
 *
 * Simple thumbs up/down feedback for A/B testing.
 * Modern visual feedback with animations.
 */

import React, { useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, IconButton, Typography, CircularProgress, Grow } from '@mui/material';
import { CheckCircleIcon, ThumbDownIcon, ThumbDownOutlinedIcon, ThumbUpIcon, ThumbUpOutlinedIcon } from "../../icons";
import { useRootStore } from '../../store/contexts';
import { EvaluationRating } from '../../api/graphql';
import type { EvaluationData } from './AiAssistantStore';

interface EvaluationFormProps {
  /** Response ID for tracking feedback */
  responseId: string;
  onSave: (data: EvaluationData) => Promise<void>;
}

export const EvaluationForm: React.FC<EvaluationFormProps> = observer(({
  responseId,
  onSave,
}) => {
  const { authStore } = useRootStore();

  // Check permission
  const canEvaluate = authStore.hasPermissions('aiSearchEvaluation_create');

  // State
  const [submittedRating, setSubmittedRating] = useState<EvaluationRating | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<EvaluationRating | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Handle rating click - save immediately
  const handleRating = useCallback(async (rating: EvaluationRating) => {
    if (isSubmitting || submittedRating || !responseId) return;

    setIsSubmitting(rating);

    try {
      await onSave({
        responseId,
        rating,
      });

      setSubmittedRating(rating);
      setShowSuccess(true);

      // Hide success message after 2 seconds
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (err) {
      console.error('Error saving evaluation:', err);
    } finally {
      setIsSubmitting(null);
    }
  }, [isSubmitting, submittedRating, responseId, onSave]);

  // Don't render if user can't evaluate or no responseId
  if (!canEvaluate || !responseId) return null;

  const isThumbsUpActive = submittedRating === EvaluationRating.ThumbsUp;
  const isThumbsDownActive = submittedRating === EvaluationRating.ThumbsDown;
  const isThumbsUpLoading = isSubmitting === EvaluationRating.ThumbsUp;
  const isThumbsDownLoading = isSubmitting === EvaluationRating.ThumbsDown;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 1,
        py: 0.75,
        px: 1.5,
        backgroundColor: 'action.hover',
        borderRadius: 2,
        transition: 'all 0.2s ease',
      }}
    >
      {/* Success state */}
      {submittedRating && showSuccess ? (
        <Grow in={showSuccess}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon
              sx={{
                color: 'success.main',
                fontSize: '1.25rem',
                animation: 'pulse 0.5s ease-in-out',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(0.8)', opacity: 0.5 },
                  '50%': { transform: 'scale(1.2)' },
                  '100%': { transform: 'scale(1)', opacity: 1 },
                },
              }}
            />
            <Typography variant="body2" color="text.secondary">
              ¡Gracias por tu feedback!
            </Typography>
          </Box>
        </Grow>
      ) : submittedRating ? (
        // Submitted state (after success animation)
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            disabled
            sx={{
              color: isThumbsUpActive ? 'success.main' : 'action.disabled',
              transform: isThumbsUpActive ? 'scale(1.1)' : 'scale(1)',
              transition: 'all 0.2s ease',
            }}
          >
            {isThumbsUpActive ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
          </IconButton>
          <IconButton
            size="small"
            disabled
            sx={{
              color: isThumbsDownActive ? 'error.main' : 'action.disabled',
              transform: isThumbsDownActive ? 'scale(1.1)' : 'scale(1)',
              transition: 'all 0.2s ease',
            }}
          >
            {isThumbsDownActive ? <ThumbDownIcon /> : <ThumbDownOutlinedIcon />}
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            Feedback enviado
          </Typography>
        </Box>
      ) : (
        // Interactive state
        <>
          <Typography variant="body2" color="text.secondary">
            ¿Fue útil?
          </Typography>

          <IconButton
            onClick={() => handleRating(EvaluationRating.ThumbsUp)}
            disabled={!!isSubmitting}
            size="small"
            sx={{
              color: 'action.active',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: 'success.main',
                backgroundColor: 'success.light',
                transform: 'scale(1.15)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            {isThumbsUpLoading ? (
              <CircularProgress size={20} color="success" />
            ) : (
              <ThumbUpOutlinedIcon />
            )}
          </IconButton>

          <IconButton
            onClick={() => handleRating(EvaluationRating.ThumbsDown)}
            disabled={!!isSubmitting}
            size="small"
            sx={{
              color: 'action.active',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: 'error.main',
                backgroundColor: 'error.light',
                transform: 'scale(1.15)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            {isThumbsDownLoading ? (
              <CircularProgress size={20} color="error" />
            ) : (
              <ThumbDownOutlinedIcon />
            )}
          </IconButton>
        </>
      )}
    </Box>
  );
});

export default EvaluationForm;
